import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import { Button } from '@repo/ui';
import { convertNumberToLocaleMoneyFormat } from '@repo/shared/helpers';
import { Limit, LimitMerchant, OfferLimit } from '@repo/shared/types';
import { useIsMobile } from '@repo/mui-config/hooks';
import { colorPalette } from '@repo/mui-config/color-palette';

import { LoanProcessStatusIconWrapper } from '@/components/loan-process/loan-process-status-icon-wrapper';
import { useLoanProcessSetCurrentModalState } from '@/components/loan-process/hooks/use-loan-process-set-current-modal-state';
import { useLoanProcessModal } from '@/components/loan-process/hooks/use-loan-process-modal';
import { LoanProcessModalHeader } from '@/components/loan-process/loan-process-modal/loan-process-modal-header';

interface ApplicationPositiveView {
  loanLimit: Limit | OfferLimit | LimitMerchant;
}

export function ApplicationPositive({ loanLimit }: ApplicationPositiveView) {
  const isMobile = useIsMobile();

  useLoanProcessSetCurrentModalState('ApplicationPositive');
  const { t } = useTranslation();
  const { setLoanStage } = useLoanProcessModal();

  return (
    <Box data-testid="application-positive" sx={{ px: isMobile ? 2 : 4, py: 4 }}>
      <LoanProcessModalHeader containerSx={{ mb: isMobile ? 4 : 8 }} />
      <Stack
        alignContent="center"
        alignItems="center"
        gap={7}
        sx={{ marginBottom: '64px', paddingX: '0px' }}
      >
        <Stack>
          <Typography component="p" textAlign="center" variant="h5">
            {t('loanProcess.loanSteps.application.positive.title.success')}
          </Typography>
          <Typography component="p" textAlign="center" variant="h5">
            {t('loanProcess.loanSteps.application.positive.title.applicationSucceed')}
          </Typography>
        </Stack>
        <LoanProcessStatusIconWrapper iconType="success">
          <CheckIcon />
        </LoanProcessStatusIconWrapper>
        {loanLimit.minMaxDifference ? (
          <Stack>
            <Typography textAlign="center">
              {t('loanProcess.loanSteps.application.positive.leaveOrIncreaseAmount.title')}
            </Typography>
            <Typography textAlign="center">
              {t('loanProcess.loanSteps.application.positive.leaveOrIncreaseAmount.newMaxAmount')}
            </Typography>
            <Typography textAlign="center" variant="h5">
              {convertNumberToLocaleMoneyFormat(loanLimit.available)} {loanLimit.currency}
            </Typography>
          </Stack>
        ) : (
          <Typography sx={{ marginY: 2 }} textAlign="center">
            {t('loanProcess.loanSteps.application.positive.description')}
          </Typography>
        )}
      </Stack>
      {loanLimit.minMaxDifference ? (
        <>
          <Button
            color="secondary"
            data-testid="application-positive-button"
            fullWidth
            onClick={() => {
              setLoanStage(2, {
                viewOptions: {
                  stageViewType: 'AGREEMENT_AND_APPROVALS',
                  AGREEMENT_AND_APPROVALS: {
                    openInEditMode: true,
                  },
                },
              });
            }}
            sx={{ paddingY: { md: '14px', xs: '8px' } }}
            variant="contained"
          >
            {t('loanProcess.loanSteps.application.positive.button.increaseAmount')}
          </Button>
          <Button
            data-testid="application-positive-button"
            fullWidth
            onClick={() => {
              setLoanStage(2, {
                viewOptions: {
                  stageViewType: 'AGREEMENT_AND_APPROVALS',
                  AGREEMENT_AND_APPROVALS: {
                    openInEditMode: false,
                  },
                },
              });
            }}
            sx={{
              paddingY: { md: '14px', xs: '8px' },
              color: colorPalette.pale.main,
            }}
            variant="text"
          >
            {t('loanProcess.loanSteps.application.positive.button.keepCurrentAmount')}
          </Button>
        </>
      ) : (
        <Button
          color="secondary"
          data-testid="application-positive-button"
          fullWidth
          onClick={() => {
            setLoanStage(2, {
              viewOptions: {
                stageViewType: 'AGREEMENT_AND_APPROVALS',
                AGREEMENT_AND_APPROVALS: {
                  openInEditMode: false,
                },
              },
            });
          }}
          sx={{ paddingY: { md: '14px', xs: '8px' } }}
          variant="contained"
        >
          {t('loanProcess.loanSteps.application.positive.button.default')}
        </Button>
      )}
    </Box>
  );
}
